import { createAsyncThunk } from '@reduxjs/toolkit';
import { UpsertTournamentTeamDto } from 'modules/teams/domain/dtos/upsert-tournament-team.dto';
import { Team } from 'modules/teams/domain/interfaces/team';
import teamsRepository from 'modules/teams/domain/repositories/teams.repository';
import teamsResource from 'modules/teams/domain/resources/teams-resource';
import { TEAMS_LIST_TAG } from 'modules/teams/store/teams-api';
import { ApiError, toApiError } from 'shared/errors';
import { baseApi } from 'shared/api/base-api';
import { getNormalizedDate } from 'shared/utils/get-normalized-date';

const PREFIX = 'teams';

/** Сбрасывает кэш карточки команды — мутации здесь thunk'и, тег сам не инвалидируется. */
const invalidateTeam = (teamId: number) => baseApi.util.invalidateTags([{ type: 'Team' as const, id: teamId }]);

/**
 * Сбрасывает перечень команд. Отдельно от `invalidateTeam`: тег с `id` и тег
 * списка в RTK Query не пересекаются, поэтому появление и удаление команды надо
 * объявлять именно тегом списка.
 */
const invalidateTeamsList = () => baseApi.util.invalidateTags([TEAMS_LIST_TAG]);

/**
 * Сохранённая команда плюс причина, по которой не загрузился логотип.
 *
 * Логотип грузится отдельным REST-запросом **после** сохранения команды. Его провал — не провал
 * сохранения: команда уже есть на бэкенде, и отклонённый thunk заставил бы пользователя нажать
 * «Сохранить» ещё раз — то есть создать дубль. Поэтому причина едет данными, а предупреждение
 * показывает экран.
 */
export interface SaveTeamResult {
  team: Team;
  logoError?: ApiError;
}

/** Грузит логотип сохранённой команды; провал возвращает причиной, а не бросает. */
async function uploadTeamLogo(team: Team, logo: File): Promise<SaveTeamResult> {
  try {
    const res = await teamsResource.uploadLogo(logo, team.id);
    return { team: { ...team, logo: { id: res.id, url: res.emblem } } };
  } catch (error) {
    console.error(`${PREFIX}/uploadLogo error:`, error);
    return { team, logoError: toApiError(error) };
  }
}

export const createTeam = createAsyncThunk<SaveTeamResult, UpsertTournamentTeamDto>(
  `${PREFIX}/createTeam`,
  async (payload, { rejectWithValue, dispatch }) => {
    try {
      const team = { name: payload.name, shortName: payload.shortName, tournamentId: payload.tournamentId };
      const teamData = await teamsRepository.upsertTeam(team);
      teamData.createdAt = getNormalizedDate(new Date());

      const result = payload.logo ? await uploadTeamLogo(teamData, payload.logo) : { team: teamData };
      dispatch(invalidateTeamsList());

      return result;
    } catch (error) {
      console.error(`${PREFIX}/createTeam error:`, error);
      return rejectWithValue(toApiError(error));
    }
  },
);

export const deleteTeam = createAsyncThunk<{ id: number }, { id: number }>(
  `${PREFIX}/deleteTeam`,
  async (payload, { rejectWithValue, dispatch }) => {
    try {
      await teamsRepository.deleteTeam(payload.id);
      dispatch(invalidateTeamsList());
      return { id: payload.id };
    } catch (error) {
      console.error(`${PREFIX}/deleteTeam error:`, error);
      return rejectWithValue(toApiError(error));
    }
  },
);

export const upsertTeamKits = createAsyncThunk<
  Team,
  { teamId: number; kits: Array<{ type: string; elements: Array<{ element: string; pattern: string; colors: string[] }> }> }
>(`${PREFIX}/upsertTeamKits`, async (payload, { rejectWithValue, dispatch }) => {
  try {
    const team = await teamsRepository.upsertTeamKits(payload.teamId, payload.kits);
    dispatch(invalidateTeam(payload.teamId));

    return team;
  } catch (error) {
    console.error(`${PREFIX}/upsertTeamKits error:`, error);
    return rejectWithValue(toApiError(error));
  }
});

export const editTeam = createAsyncThunk<SaveTeamResult, UpsertTournamentTeamDto>(
  `${PREFIX}/editTeam`,
  async (payload, { rejectWithValue, dispatch }) => {
    try {
      const team = { id: payload.id, name: payload.name, shortName: payload.shortName, tournamentId: payload.tournamentId };
      const teamData = await teamsRepository.upsertTeam(team);

      const result = payload.logo instanceof File ? await uploadTeamLogo(teamData, payload.logo) : { team: teamData };
      dispatch(invalidateTeam(teamData.id));

      return result;
    } catch (error) {
      console.error(`${PREFIX}/editTeam error:`, error);
      return rejectWithValue(toApiError(error));
    }
  },
);
